import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { SendOutlined, DeleteOutlined, BellOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import manufacturerNotificationApi from "../../../api/manufacturerNotification/manufacturerNotification";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import RowActions from "../../../components/cms/RowActions";
import { ACTION_ICON, confirmDelete } from "../../../components/cms/actions";
import { neutral } from "../../../theme/tokens";
import StatusTag from "../../../components/cms/StatusTag";

const { Text } = Typography;
const { TextArea } = Input;

// Vai trò khách (khớp Customer::TYPE_* ở BE)
const ROLE_OPTIONS = [
  { value: "CONTRACTOR", label: "Thợ thầu" },
  { value: "DEALER", label: "Đại lý" },
  { value: "NPP", label: "Nhà phân phối (NPP)" },
];

const TARGET_OPTIONS = [
  { value: "ALL", label: "Toàn bộ khách" },
  { value: "ROLE", label: "Theo vai trò" },
  { value: "TIER", label: "Theo hạng" },
  { value: "CUSTOMER", label: "Chọn khách" },
];

const TARGET_LABEL = {
  ALL: "Toàn bộ",
  ROLE: "Theo vai trò",
  TIER: "Theo hạng",
  CUSTOMER: "Khách chọn",
};

// Trích mảng dữ liệu dù BE trả [...] hay {data:[...]} hay {data:{data:[...]}}
const extractArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const ManufacturerNotificationPage = () => {
  const [form] = Form.useForm();
  const targetType = Form.useWatch("target_type", form);

  const [classes, setClasses] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const searchTimer = useRef(null);

  const fetchList = async (page = pagination.current, size = pagination.pageSize) => {
    setListLoading(true);
    try {
      const payload = await manufacturerNotificationApi.getList({
        has_paginate: 1,
        size,
        page,
      });
      setList(payload?.data || []);
      setPagination((prev) => ({
        ...prev,
        current: payload?.current_page || page,
        pageSize: payload?.per_page || size,
        total: payload?.total || 0,
      }));
    } catch (e) {
      message.error(e?.response?.data?.message || "Không tải được danh sách thông báo");
    } finally {
      setListLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const payload = await manufacturerNotificationApi.getMembershipClasses();
      const opts = extractArray(payload).map((c) => ({ value: c.id, label: c.name }));
      setClasses(opts);
    } catch (e) {
      // hạng là tùy chọn, không chặn trang
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchList(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchCustomer = (value) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!value || value.trim().length < 2) {
      setCustomerOptions([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setCustomerLoading(true);
      try {
        const payload = await manufacturerNotificationApi.searchCustomers(value.trim());
        const opts = extractArray(payload).map((c) => ({
          value: c.id,
          label: `${c.name || "Khách"}${c.phone ? " - " + c.phone : ""}`,
        }));
        setCustomerOptions(opts);
      } catch (e) {
        setCustomerOptions([]);
      } finally {
        setCustomerLoading(false);
      }
    }, 350);
  };

  const onFinish = async (values) => {
    const payload = {
      title: values.title,
      message: values.message,
      target_type: values.target_type,
      sender: values.sender?.trim() || undefined,
    };
    if (values.target_type !== "ALL") {
      payload.target_value = values.target_value;
    }

    setSubmitting(true);
    try {
      await manufacturerNotificationApi.create(payload);
      message.success("Đã gửi thông báo");
      form.resetFields();
      form.setFieldsValue({ target_type: "ALL", sender: "Shinko Việt Nam" });
      fetchList(1, pagination.pageSize);
    } catch (e) {
      const errors = e?.response?.data?.errors;
      if (errors) {
        form.setFields(
          Object.keys(errors).map((name) => ({ name, errors: [errors[name][0]] }))
        );
      } else {
        message.error(e?.response?.data?.message || "Gửi thông báo thất bại");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await manufacturerNotificationApi.remove(id);
      message.success("Đã xóa");
      const nextPage = list.length === 1 && pagination.current > 1 ? pagination.current - 1 : pagination.current;
      fetchList(nextPage, pagination.pageSize);
    } catch (e) {
      message.error(e?.response?.data?.message || "Xóa thất bại");
    }
  };

  // Nhãn giá trị đối tượng gửi cho từng dòng trong bảng
  const renderTargetValue = (record) => {
    const tv = record.target_value;
    if (record.target_type === "ALL" || !tv || !tv.length) return null;
    if (record.target_type === "ROLE") {
      const map = { CONTRACTOR: "Thợ thầu", DEALER: "Đại lý", NPP: "NPP" };
      return tv.map((v) => map[v] || v).join(", ");
    }
    if (record.target_type === "TIER") {
      const map = Object.fromEntries(classes.map((c) => [c.value, c.label]));
      return tv.map((v) => map[v] || `#${v}`).join(", ");
    }
    if (record.target_type === "CUSTOMER") return `${tv.length} khách`;
    return null;
  };

  const columns = useMemo(
    () => [
      {
        title: "Tiêu đề",
        dataIndex: "title",
        key: "title",
        render: (v, r) => (
          <div>
            <Text strong>{v}</Text>
            <div style={{ color: neutral[500], fontSize: 12, marginTop: 2 }}>{r.message}</div>
          </div>
        ),
      },
      {
        title: "Đối tượng",
        key: "target",
        width: 200,
        render: (_, r) => (
          <Space direction="vertical" size={2}>
            <StatusTag>{TARGET_LABEL[r.target_type] || r.target_type}</StatusTag>
            {renderTargetValue(r) ? (
              <Text style={{ fontSize: 12, color: neutral[500] }}>{renderTargetValue(r)}</Text>
            ) : null}
          </Space>
        ),
      },
      {
        title: "Đã đọc",
        dataIndex: "reads_count",
        key: "reads_count",
        width: 90,
        align: "center",
        render: (v) => v ?? 0,
      },
      {
        title: "Người gửi",
        dataIndex: "sender",
        key: "sender",
        width: 150,
      },
      {
        title: "Thời gian",
        dataIndex: "created_at",
        key: "created_at",
        width: 160,
        render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : ""),
      },
      {
        title: "Thao tác",
        key: "action",
        width: 80,
        align: "center",
        render: (_, r) => (
          <RowActions
            items={[
              {
                key: "delete",
                icon: ACTION_ICON.delete,
                label: "Xóa thông báo",
                danger: true,
                onClick: () =>
                  confirmDelete(r.title || `thông báo #${r.id}`, () => handleDelete(r.id)),
              },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classes, list, pagination]
  );

  return (
    <BrandedPageLayout
      title="Thông báo NSX"
      subtitle="Soạn và gửi thông báo tới khách hàng trên mini app"
      breadcrumbItems={[{ title: "Thông báo NSX" }]}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={9}>
          <Card title="Soạn thông báo" bordered={false} style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <Form
              form={form}
              layout="vertical"
              initialValues={{ target_type: "ALL", sender: "Shinko Việt Nam" }}
              onFinish={onFinish}
            >
              <Form.Item
                label="Tiêu đề"
                name="title"
                rules={[{ required: true, message: "Nhập tiêu đề" }, { max: 191, message: "Tối đa 191 ký tự" }]}
              >
                <Input placeholder="VD: Chương trình ưu đãi tháng 8" maxLength={191} />
              </Form.Item>

              <Form.Item label="Nội dung" name="message" rules={[{ required: true, message: "Nhập nội dung" }]}>
                <TextArea rows={4} placeholder="Nội dung thông báo gửi tới khách..." showCount maxLength={1000} />
              </Form.Item>

              <Form.Item label="Đối tượng nhận" name="target_type" rules={[{ required: true }]}>
                <Radio.Group
                  options={TARGET_OPTIONS}
                  optionType="button"
                  buttonStyle="solid"
                  onChange={() => form.setFieldsValue({ target_value: undefined })}
                />
              </Form.Item>

              {targetType === "ROLE" && (
                <Form.Item
                  label="Vai trò"
                  name="target_value"
                  rules={[{ required: true, message: "Chọn ít nhất 1 vai trò" }]}
                >
                  <Select mode="multiple" placeholder="Chọn vai trò" options={ROLE_OPTIONS} allowClear />
                </Form.Item>
              )}

              {targetType === "TIER" && (
                <Form.Item
                  label="Hạng thành viên"
                  name="target_value"
                  rules={[{ required: true, message: "Chọn ít nhất 1 hạng" }]}
                >
                  <Select mode="multiple" placeholder="Chọn hạng" options={classes} allowClear />
                </Form.Item>
              )}

              {targetType === "CUSTOMER" && (
                <Form.Item
                  label="Khách hàng"
                  name="target_value"
                  rules={[{ required: true, message: "Chọn ít nhất 1 khách" }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Nhập tên/SĐT để tìm..."
                    showSearch
                    filterOption={false}
                    onSearch={handleSearchCustomer}
                    loading={customerLoading}
                    options={customerOptions}
                    notFoundContent={customerLoading ? "Đang tìm..." : "Nhập ≥ 2 ký tự để tìm"}
                    allowClear
                  />
                </Form.Item>
              )}

              <Form.Item label="Người gửi (hiển thị)" name="sender">
                <Input placeholder="Shinko Việt Nam" maxLength={191} />
              </Form.Item>

              <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={submitting} block>
                Gửi thông báo
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={15}>
          <Card
            title={
              <Space>
                <BellOutlined /> Đã gửi
              </Space>
            }
            bordered={false}
            style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
          >
            <Table
              scroll={{ x: "max-content" }}
              rowKey="id"
              columns={columns}
              dataSource={list}
              loading={listLoading}
              size="middle"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                showTotal: (t, r) => `Hiển thị ${r[0]}-${r[1]} trong tổng ${t} bản ghi`,
                onChange: (page, size) => fetchList(page, size),
              }}
            />
          </Card>
        </Col>
      </Row>
    </BrandedPageLayout>
  );
};

export default ManufacturerNotificationPage;
